/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useState } from "react";

import * as React from "react";
import { ProcessingState } from "components/EnhancedSpeech/useEnhanceSpeech";
import styled from "styled-components";
import infoIconUrl from "../../images/info.svg";
import { useJitterInterval } from "hooks/useJitterInterval";
import {
  userMessageForEnhanceTrackError,
  EnhanceSpeechTrackErrorType,
  getIntervalDescription,
} from "components/EnhancedSpeech/Utilities";
import { useEnhanceSpeechLimits } from "components/EnhancedSpeech/useEnhanceSpeechLimits";
import { SWCTooltip } from "components/shared/Tooltips";
import { fontFamilies } from "components/shared/Styles";

export interface TrackItemStatusProps {
  processingState: ProcessingState;
  duration: number | null;
  retryAtTime: Date | null;
  errorType: EnhanceSpeechTrackErrorType | null;
}

export const TrackItemStatus: React.FC<TrackItemStatusProps> = ({
  processingState,
  duration,
  retryAtTime,
  errorType,
}) => {
  const retryAtIntervalDescription = useRetryAtIntervalDescription(retryAtTime);
  const userLimits = useEnhanceSpeechLimits();

  switch (processingState) {
    case ProcessingState.paused:
      return (
        <TrackItemStatusContainer>
          {I18n.t("enhance_speech.premium.status.paused")}
          <overlay-trigger>
            <img src={infoIconUrl} slot="trigger" alt="" />
            <SWCTooltip slot="hover-content" open>
              {I18n.t("enhance_speech.premium.status.paused_tooltip", {
                intervalDescription: retryAtIntervalDescription,
              })}
            </SWCTooltip>
          </overlay-trigger>
        </TrackItemStatusContainer>
      );
    case ProcessingState.finished:
      if (errorType) {
        return (
          <TrackItemStatusContainer data-testid={errorType}>
            {I18n.t("enhance_speech.premium.status.failed")}
            <overlay-trigger>
              <img src={infoIconUrl} slot="trigger" alt="" />
              <SWCTooltip slot="hover-content" open>
                {userMessageForEnhanceTrackError(
                  errorType,
                  userLimits,
                  retryAtIntervalDescription
                )}
              </SWCTooltip>
            </overlay-trigger>
          </TrackItemStatusContainer>
        );
      } else {
        return (
          <TrackItemStatusContainer>
            {getProcessingStatus(processingState, duration)}
          </TrackItemStatusContainer>
        );
      }
    default:
      return (
        <TrackItemStatusContainer>
          {getProcessingStatus(processingState, duration)}
        </TrackItemStatusContainer>
      );
  }
};

const useRetryAtIntervalDescription = (retryAtTime: Date | null): string => {
  // refresh to give an updated interval
  const [_, setTick] = useState(0);
  useJitterInterval(() => setTick((n) => n + 1), 1000, 0);

  return getIntervalDescription(retryAtTime?.toISOString());
};

const getProcessingStatus = (
  processingState: Exclude<ProcessingState, ProcessingState.paused>,
  duration: number | null
): string => {
  switch (processingState) {
    case ProcessingState.processing:
      return I18n.t("enhance_speech.premium.status.processing");
    case ProcessingState.finished:
      const durationSeconds = duration;
      if (durationSeconds !== null && durationSeconds > 3600) {
        return new Date(durationSeconds * 1000).toISOString().substring(11, 19);
      } else if (durationSeconds !== null) {
        return new Date(durationSeconds * 1000).toISOString().substring(14, 19);
      }

      return "";
    case ProcessingState.queued:
      return I18n.t("enhance_speech.premium.status.queued");
    case ProcessingState.uploading:
      return I18n.t("enhance_speech.premium.status.uploading");
  }
};

const TrackItemStatusContainer = styled.span`
  font: normal normal normal 16px/20px ${fontFamilies};

  display: flex;
  flex-direction: row;
  white-space: nowrap;
  gap: 8px;
  align-items: center;

  & > overlay-trigger {
    height: 18px;
  }
`;
